import { api } from '@/lib/axios';
import type { AuthUser, UserRole } from '@/store/authSlice';
import type { Meeting } from '@/features/meetings/meeting.api';

export type UserStatus = 'ACTIVE' | 'SUSPENDED' | 'PENDING' | 'DELETED';

export interface AdminSubscription {
  id: string;
  tier: string;
  status: string;
  currentPeriodEnd: string | null;
  user: { email: string; firstName: string; lastName: string };
}

export interface AuditLog {
  id: string;
  userId: string | null;
  action: string;
  resource: string | null;
  ipAddress: string | null;
  createdAt: string;
}

interface Paged<T> {
  items: T[];
  total: number;
}

export const adminApi = {
  async users(params: { page?: number; search?: string } = {}): Promise<Paged<AuthUser & { status: UserStatus }>> {
    const { data } = await api.get('/admin/users', { params });
    return { items: data.data.users, total: data.meta?.total ?? 0 };
  },
  async setRole(id: string, role: UserRole): Promise<void> {
    await api.patch(`/admin/users/${id}/role`, { role });
  },
  async setStatus(id: string, status: UserStatus): Promise<void> {
    await api.patch(`/admin/users/${id}/status`, { status });
  },
  async meetings(params: { page?: number; search?: string } = {}): Promise<Paged<Meeting>> {
    const { data } = await api.get('/admin/meetings', { params });
    return { items: data.data.meetings, total: data.meta?.total ?? 0 };
  },
  async endMeeting(id: string): Promise<void> {
    await api.post(`/admin/meetings/${id}/end`);
  },
  async subscriptions(params: { page?: number } = {}): Promise<Paged<AdminSubscription>> {
    const { data } = await api.get('/admin/subscriptions', { params });
    return { items: data.data.subscriptions, total: data.meta?.total ?? 0 };
  },
  async auditLogs(params: { page?: number; action?: string } = {}): Promise<Paged<AuditLog>> {
    const { data } = await api.get('/admin/audit-logs', { params });
    return { items: data.data.logs, total: data.meta?.total ?? 0 };
  },
};
